/**
 * Watch Registration Status
 *
 * Maps My Account App warranty/registration status codes to UI outcomes.
 * The numeric envelope `code` is the domain result, not an HTTP status.
 *
 * Known codes: 200 ActiveAndUnbound (registerable), 801 not found / not
 * activated, 810 registered by this account, 811 registered by another
 * account, 701 invalid request. Any other code (the backend may add more)
 * falls back to the generic "not found" message.
 */

import { WarrantyStatus, type Watch } from './types';

/** My Watches list after a successful bind — drives the success tile. */
export const MY_WATCHES_REGISTERED_HREF = '/my-account/my-watches?registered=true';

/**
 * Warranty codes the EW flow treats as a presentable watch: registerable,
 * already yours, or owned by someone else. Anything else is a dead scan.
 */
export const VALID_WARRANTY_STATUS_CODES: readonly number[] = [
  WarrantyStatus.ActiveAndUnbound,
  WarrantyStatus.RegisteredBySelf,
  WarrantyStatus.RegisteredByOther,
];

/** i18n error-message keys under `myAccount.myWatchesPage.registration.errors`. */
export type RegistrationErrorKey =
  | 'notActivated'
  | 'registeredBySelf'
  | 'registeredByOther'
  | 'wrongBrand'
  | 'notFound';

/** A warranty record can be registered when visible and not yet bound. */
export function isRegisterable(data: Pick<Watch, 'is_visible' | 'is_bind'> | undefined): boolean {
  return Boolean(data?.is_visible && !data.is_bind);
}

/**
 * Map a My Account App status code to the registration error message key.
 * 200 is a success — callers should not reach here with it, but mapping it
 * to the generic message keeps the return total.
 */
export function getRegistrationErrorKey(code: number | undefined): RegistrationErrorKey {
  switch (code) {
    case WarrantyStatus.NotFound:
      return 'notActivated';
    case WarrantyStatus.RegisteredBySelf:
      return 'registeredBySelf';
    case WarrantyStatus.RegisteredByOther:
      return 'registeredByOther';
    case WarrantyStatus.BrandMismatch:
      return 'wrongBrand';
    default:
      return 'notFound';
  }
}
